import { FormikHelpers } from 'formik'
import * as React from 'react'
import styled from 'styled-components'

import { LoginHeading } from '@ally/ally-login'
import { Form, Forms, useFormsContext } from '@ally/forms'
import {
  Button,
  CardSelectionCard,
  CardSelectionGroup,
  ScreenReaderOnly,
  Space,
} from '@ally/metronome-ui'
import {
  AsyncStatus,
  LoginAccountTypeSelectorProps,
} from '@ally/transmitigator'

import { useHostServices } from '../../HostServices'
import { useTransmit } from '../../TransmitContext'
import { CIAMErrorBanner, StyledTextBody } from '../Shared'

const ACCT_TYPE_AUTO = 'auto'
const ACCT_TYPE_NOT_AUTO = 'not_auto'

type LoginAccountTypeOption = typeof ACCT_TYPE_AUTO | typeof ACCT_TYPE_NOT_AUTO

const StyledCardSelectionGroup = styled(CardSelectionGroup)`
  ${({ theme: { media, space } }): string => `
    ${media.up('md')} {
      max-width: 300px;
    }
    margin-bottom: ${space.lg}
  `}
`

interface LoginAccountTypeSelectorFormProps {
  formId: string
  status: AsyncStatus
}

const LoginAccountTypeSelectorForm: React.FC<LoginAccountTypeSelectorFormProps> = ({
  formId,
  status,
}) => {
  const { setFieldValue } = useFormsContext()
  const { analytics } = useHostServices()

  React.useEffect(() => {
    analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Account Type',
    })
  }, [])

  return (
    <Form id={formId}>
      <ScreenReaderOnly id="select-account-type-label">
        Which type of account are you signing in to?
      </ScreenReaderOnly>
      <StyledCardSelectionGroup
        variant="single-select"
        align="stacked"
        defaultSelection={ACCT_TYPE_NOT_AUTO}
        id="selectAccountType"
        ariaLabelledby="select-account-type-label"
        onSelection={(cardId: any): void => {
          setFieldValue('accountType', cardId)
        }}
      >
        <CardSelectionCard
          cardId={ACCT_TYPE_NOT_AUTO}
          title="Bank, Invest, Credit Card or Home Loans "
        />
        <CardSelectionCard cardId={ACCT_TYPE_AUTO} title="Auto" />
      </StyledCardSelectionGroup>

      <Button
        allytmln="continue"
        text="Continue"
        loading={status === 'Loading'}
        type="submit"
        variant="primary"
      />
    </Form>
  )
}

interface LoginAccountTypeSelectorFormState {
  accountType: LoginAccountTypeOption
}

const LoginAccountTypeSelectorContent: React.FC<LoginAccountTypeSelectorProps> = ({
  onComplete,
  status,
  error,
}) => {
  const formId = 'login-account-type-selector-form'
  const initialValues: LoginAccountTypeSelectorFormState = {
    accountType: ACCT_TYPE_NOT_AUTO, // defaults to not auto
  }
  const handleSubmit = (
    formValues: LoginAccountTypeSelectorFormState,
    { setSubmitting }: FormikHelpers<LoginAccountTypeSelectorFormState>,
  ): void => {
    onComplete({
      isAuto: formValues.accountType === ACCT_TYPE_AUTO,
    })

    setSubmitting(false)
  }

  return (
    <>
      <LoginHeading title="Which type of account are you signing in to?" />

      <Space mb="lg">
        <StyledTextBody size="sm" tag="p">
          We won&apos;t always ask this &ndash; just while we complete some
          enhancements.
        </StyledTextBody>
      </Space>

      <CIAMErrorBanner error={error} />

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <LoginAccountTypeSelectorForm formId={formId} status={status} />
      </Forms>
    </>
  )
}

export const LoginAccountTypeSelector: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'LoginAccountTypeSelector') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <LoginAccountTypeSelectorContent {...activeScreen.payload} />
}
